import * as Cesium from 'cesium';
import { makeStyles } from "@mui/styles";
import { useDispatch } from "react-redux";
import FormControl from '@mui/material/FormControl';
import useViewerStore from '../../store-zustand/viewer';
import { radians_to_degrees } from "../MapView/MapUtils";
import useTaskingStore from '../../store-zustand/tasking';
import React, { useState, useRef, useEffect } from "react";
import TooltipIconButton from "components/TooltipIconButton";
import { MenuItem, Select, CircularProgress } from '@mui/material';
import { Content, FormItem, Indicators, Footer, Wrapper } from '../ActionModal';
import { getGlobePositionWithTerrain, generateNodeImage, ToolTipDisplay } from '../MapView/MapUtils';
import { Close, DataSaverOffOutlined, HistoryOutlined, CreditCardOutlined, ScheduleSendOutlined, CheckCircleOutline, CropOutlined } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: 0,
    minWidth: 131,
    position: 'relative'
  }
}));

const resolutions = [
  { value: 3, label: '3cm GSD' },
  { value: 5, label: '5cm GSD' },
  { value: 10, label: '10cm GSD' },
  { value: 15, label: '15cm GSD' }
];

const bundles = [
  { value: 1, label: 'Basic' },
  { value: 2, label: 'Premium' },
  { value: 3, label: 'Ultimate' },
]

const updates = [
  { value: 1, label: 'Once' },
  { value: 2, label: 'Daily' },
  { value: 3, label: 'Weekly' },
  { value: 4, label: 'Monthly' },
  { value: 5, label: 'Quarterly' },
  { value: 6, label: 'Semi-Annual' },
  { value: 7, label: 'Annual' },
]

const CityScanModal = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [formData, setFormData] = useState({
    resolution: 5,
    bundle: 1,
    update: 1
  });
  const mouseHandler = useRef();
  const polygonPositions = useRef([]);
  const polygonAdded = useRef(false);
  const closePolygon = useRef(false);
  const [cityScanPolygonAdded, setCityScanPolygonAdded] = useState(false);
  const { setmapboxvisibility } = useViewerStore();
  const { createTask } = useTaskingStore();
  const [notifyCaptureSubmission, setNotifyCaptureSubmission] = useState(false);
  const [captureSubmitted, setCaptureSubmitted] = useState(false);

  const handleChange = (event, name) => {
    setFormData({ ...formData, [name]: event.target.value });
  };

  const closeModal = () => {
    if (mouseHandler.current) {
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
    }

    window.MAIN_CESIUM_VIEWER.entities.remove(window.cityScanPolygon);

    delete (window.cityScanPolygon);
    if (window.cityScanPolygonNodes) {
      window.cityScanPolygonNodes.map(node => {
        window.MAIN_CESIUM_VIEWER.entities.remove(node);
      })
      delete (window.cityScanPolygonNodes);
    }
    polygonPositions.current = [];
    polygonAdded.current = false;

    document.getElementById("cesium-wrapper").style.cursor = 'auto';

    setCityScanPolygonAdded(false);
    dispatch({ type: 'SET_CITY_SCAN_ACTIVE', cityScanActive: false })


    setmapboxvisibility(0);
  }

  const drawPolygon = () => {
    document.getElementById("cesium-wrapper").style.cursor = 'crosshair';
    if (mouseHandler.current) return
    mouseHandler.current = new Cesium.ScreenSpaceEventHandler(window.MAIN_CESIUM_VIEWER.scene.canvas);

    mouseHandler.current.setInputAction(function (movement) {
      if (polygonPositions.current.length == 0) {
        ToolTipDisplay(1, movement.endPosition, 'Click for starting point');
      } else {
        var firstPos = Cesium.SceneTransforms.wgs84ToDrawingBufferCoordinates(window.MAIN_CESIUM_VIEWER.scene, Cesium.Cartographic.toCartesian(polygonPositions.current[0]))
        var screenDistance = Cesium.Cartesian2.distance(firstPos, movement.endPosition);
        ToolTipDisplay(2);

        if (screenDistance <= 15) {
          ToolTipDisplay(1, movement.endPosition, 'Click to close Polygon');
          closePolygon.current = true;
        } else {
          closePolygon.current = false;
        }
      }
      let newPos = getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, movement.endPosition);
      if (polygonPositions.current.length >= 2 && !polygonAdded.current) {
        polygonPositions.current.pop()
      }
      if (polygonPositions.current.length >= 1 && !polygonAdded.current) {
        polygonPositions.current.push(newPos);
      }

      if (polygonPositions.current.length >= 2 && !polygonAdded.current) {
        if (window.cityScanPolygon) return
        window.cityScanPolygon = window.MAIN_CESIUM_VIEWER.entities.add({
          polygon: {
            hierarchy: new Cesium.CallbackProperty(() => {
              return new Cesium.PolygonHierarchy(Cesium.Ellipsoid.WGS84.cartographicArrayToCartesianArray(polygonPositions.current));
            }, false),
            material: new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
          },
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              return Cesium.Ellipsoid.WGS84.cartographicArrayToCartesianArray([...polygonPositions.current, polygonPositions.current[0]]);
            }, false),
            width: 1.5,
            material: new Cesium.Color.fromCssColorString('#487BF2'),
            clampToGround: true
          }
        });
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    mouseHandler.current.setInputAction(function (click) {
      if (closePolygon.current) {
        polygonPositions.current.pop();
        polygonAdded.current = true;
        closePolygon.current = false;
        ToolTipDisplay(2);
        setCityScanPolygonAdded(true);
        removeMouseHandlers();
      } else {
        var position = getGlobePositionWithTerrain(window.MAIN_CESIUM_VIEWER, click.position);
        polygonPositions.current.push(position);
        if (!window.cityScanPolygonNodes) window.cityScanPolygonNodes = [];

        let circleImage = generateNodeImage('#487BF2', true);
        var node = window.MAIN_CESIUM_VIEWER.entities.add({
          position: Cesium.Cartographic.toCartesian(position),
          billboard: {
            image: circleImage,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            width: 15,
            height: 15,
            heightReference: Cesium.HeightReference.NONE,
            disableDepthTestDistance: Number.POSITIVE_INFINITY
          }
        });
        window.cityScanPolygonNodes.push(node)
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  }

  const undoPolygon = () => {
    window.MAIN_CESIUM_VIEWER.entities.remove(window.cityScanPolygon);
    delete (window.cityScanPolygon);

    window.cityScanPolygonNodes.map(node => {
      window.MAIN_CESIUM_VIEWER.entities.remove(node);
    })
    delete (window.cityScanPolygonNodes);

    polygonPositions.current = [];
    polygonAdded.current = false;

    document.getElementById("cesium-wrapper").style.cursor = 'auto';

    setCityScanPolygonAdded(false);
  }

  const removeMouseHandlers = () => {
    if (mouseHandler.current) {
      document.getElementById("cesium-wrapper").style.cursor = 'auto';
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      mouseHandler.current.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_CLICK);
      mouseHandler.current = mouseHandler.current && mouseHandler.current.destroy();
    }
  }

  const submitCapture = () => {
    let shapeString = `POLYGON((`;
    polygonPositions.current.map((cartoPoint, index) => {
      let lon = radians_to_degrees(cartoPoint.longitude);
      let lat = radians_to_degrees(cartoPoint.latitude);
      let height = cartoPoint.height;
      if (index < polygonPositions.current.length - 1) {
        shapeString = `${shapeString}${lon} ${lat} ${height},`;
      } else {
        let firstpoint = polygonPositions.current[0];
        let firstpointLon = radians_to_degrees(firstpoint.longitude);
        let firstpointLat = radians_to_degrees(firstpoint.latitude);
        let firstpointHeight = firstpoint.height;
        shapeString = `${shapeString}${lon} ${lat} ${height},${firstpointLon} ${firstpointLat} ${firstpointHeight}`;
      }
    });
    shapeString = `${shapeString}))`;
    const resolution = resolutions.find(m => m.value == formData.resolution).label;
    const bundle = bundles.find(m => m.value == formData.bundle).label;
    const update = updates.find(m => m.value == formData.update).label;

    setNotifyCaptureSubmission(true);

    createTask({
      type: "CityScan",
      Resolution: resolution,
      Bundle: bundle,
      Update: update,
      polygon: shapeString
    }).then((response) => {
      if (response == "Task created") {
        setNotifyCaptureSubmission(false);
        setCaptureSubmitted(true);
      }
    })
  }

  useEffect(() => {
    removeMouseHandlers();
    setmapboxvisibility(1);

    return (closeModal)
  }, [])

  return (
    <Wrapper>
      <TooltipIconButton title="Close" ariaLabel="close" placement="bottom" onClick={closeModal} size={44} position="true" top={10} right={10}>
        <Close />
      </TooltipIconButton>
      <Content>
        {!cityScanPolygonAdded ?
          <TooltipIconButton title="Draw Polygon" ariaLabel="draw-area" placement="top" size={60} position="true" top={-29} right={30} bluebg="true" onClick={drawPolygon}>
            <CropOutlined />
          </TooltipIconButton> :
          <TooltipIconButton title="Undo Polygon" ariaLabel="MarkerEdit" placement="top" size={60} position="true" top={-29} right={30} bluebg="true" onClick={undoPolygon}>
            <HistoryOutlined />
          </TooltipIconButton>}
        <h3>CityScan</h3>
        <FormItem>
          <p>Resolution</p>
          <FormControl variant="filled" className={classes.formControl}>
            <Select value={formData.resolution} onChange={(e) => handleChange(e, 'resolution')}>
              {resolutions.map((item, index) => (
                <MenuItem value={item.value} key={index}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormItem>
        <FormItem>
          <p>Bundle</p>
          <FormControl variant="filled" className={classes.formControl}>
            <Select value={formData.bundle} onChange={(e) => handleChange(e, 'bundle')}>
              {bundles.map((item, index) => (
                <MenuItem value={item.value} key={index}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormItem>
        <FormItem>
          <p>Updates</p>
          <FormControl variant="filled" className={classes.formControl}>
            <Select value={formData.update} onChange={(e) => handleChange(e, 'update')}>
              {updates.map((item, index) => (
                <MenuItem value={item.value} key={index}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormItem>
      </Content>
      <Indicators style={{ background: '#2F2F2F' }}>
        <div className="half-section border-right">
          <DataSaverOffOutlined sx={{ color: 'rgb(227 171 84)', fontSize: '24px' }} />
          <p>- <span>km²</span></p>
        </div>
        <div className="half-section">
          <CreditCardOutlined sx={{ color: 'rgb(22 198 125)', fontSize: '24px' }}/>
          <p>- <span>$</span></p>
        </div>
      </Indicators>
      <button onClick={submitCapture} style={{ width: "100%", padding: 0, background: '#2F2F2F' }}>
        <Footer>
          {!notifyCaptureSubmission ?
            !captureSubmitted ?
              <>
                <ScheduleSendOutlined sx={{ color: 'white', fontSize: '24px' }}/>
                <span>Submit for Capture</span>
              </>
              :
              <>
                <CheckCircleOutline sx={{ color: '#15C67D', fontSize: '24px' }}/>
                <span style={{ color: "#15C67D" }}>Capture Submitted</span>
              </> 
              :
              <>
                <CircularProgress style={{ color: "#487BF2", width: 24, height: 24 }} />
                <span>Submitting for Capture</span>
              </>
          }
        </Footer>
      </button>
    </Wrapper>
  );
};

export default CityScanModal;